// src/telemetry/capabilities.ts
// Dependency-free capability collector tuned for TV browsers + Unity WebGL

type Maybe<T> = T | null;

export type CapabilityReport = {
    timestamps: { collectedAtIso: string };
    userAgent: string;
    userAgentData?: {
        brands?: Array<{ brand: string; version: string }>;
        mobile?: boolean;
        platform?: string;
        architecture?: string;
        model?: string;
        bitness?: string;
        platformVersion?: string;
        fullVersionList?: Array<{ brand: string; version: string }>;
    };
    tvHeuristics: {
        isTizen: Maybe<string>;
        isWebOS: Maybe<string>;
        isOrsay: boolean;
        isHbbTV: Maybe<string>;
        isOperaTV: boolean;
        isRoku: boolean;
        isAmazonSilk: boolean;
        isAndroidWebView: boolean;
        isChromecast: boolean;
        isAndroidTV: boolean;
        isFireTV: boolean;
        fireTVModel: Maybe<string>;
        forceLegacy: boolean;
        reason?: string;
    };
    /** Derived, single-string classification suitable for server 'DeviceType' field */
    derivedDeviceType: "Tizen" | "WebOS" | "Roku" | "Chromecast" | "AndroidTV" | "FireTV" | "FireTV (Silk)" | "FireTV (WebView)" | "Desktop" | "Tablet" | "Mobile" | "Other";
    classification: {
        tier: "legacy" | "baseline" | "modern";
    };
    environment: {
        platform: Maybe<string>;
        vendor: Maybe<string>;
        languages: string[]; // mutable copy
        language: Maybe<string>;
        hardwareConcurrency: Maybe<number>;
        deviceMemoryGB: Maybe<number>;
        maxTouchPoints: Maybe<number>;
        pointerTypes: string[];
        hoverSupport: string[];
        cookieEnabled: boolean;
        doNotTrack: Maybe<string>;
        timezone: Maybe<string>;
        colorDepth: Maybe<number>;
        pixelRatio: Maybe<number>;
        screen: {
            width: Maybe<number>;
            height: Maybe<number>;
            availWidth: Maybe<number>;
            availHeight: Maybe<number>;
        };
        window: {
            innerWidth: Maybe<number>;
            innerHeight: Maybe<number>;
        };
    };
    features: {
        // Core
        promises: boolean;
        fetch: boolean;

        // WASM (detailed)
        wasm: boolean;                     // baseline wasm works (instantiate)
        wasmValidate: boolean;             // validate() worked
        wasmInstantiateStreaming: boolean; // instantiateStreaming available
        wasmThreadsAvailable: boolean;     // engine can create shared Memory
        wasmThreadsUsable: boolean;        // usable in this context (SAB + COI + Atomics)
        sharedArrayBuffer: boolean;
        crossOriginIsolated: boolean;

        // Graphics
        webgl1: boolean;
        webgl2: boolean;
        webgpu: boolean;

        // Platform
        mse: boolean;
        eme: boolean;
        serviceWorker: boolean;
        notifications: boolean;
        webAudio: boolean;
        offscreenCanvas: boolean;
        webRTC: boolean;
        idb: boolean;
        localStorage: boolean;
        sessionStorage: boolean;

        // CSS
        css: {
            grid: boolean;
            flexGap: boolean;
            sticky: boolean;
        };
    };
    webglInfo?: {
        renderer?: string;
        vendor?: string;
        version?: string;
        shadingLanguageVersion?: string;
        extensions?: string[];
    };
    mediaSupport: {
        video: {
            h264: boolean;
            vp9: boolean;
            av1: boolean;
            hevc: boolean;
            hls: boolean;
        };
        audio: {
            aac: boolean;
            mp3: boolean;
            opus: boolean;
            flac: boolean;
            vorbis: boolean;
        };
        mediaCapabilities?: {
            supported: boolean;
        };
        emeKeySystems: {
            widevine: boolean | "maybe";
            playready: boolean | "maybe";
            fairplay: boolean | "maybe";
        };
    };
    network?: {
        effectiveType?: string;
        downlinkMbps?: number;
        rttMs?: number;
        saveData?: boolean;
    };
    battery?: {
        charging?: boolean;
        level?: number; // 0..1
    };
    storage?: {
        quotaBytes?: number;
        usageBytes?: number;
        persisted?: boolean;
    };
    appMemory?: {
        jsHeapUsedBytes?: number;
        jsHeapTotalBytes?: number;
        jsHeapLimitBytes?: number;
        /** Chrome-only experimental total memory for this origin */
        uaSpecificTotalBytes?: number;
        /** Unity/wasm heap size if detectable */
        wasmHeapBytes?: number;
        /** Convenience: estimated remaining JS heap (limit - used) */
        jsHeapApproxFreeBytes?: number;
    };
    permissions?: Record<string, string>; // "granted" | "denied" | "prompt" | "unknown"
    pluginsSummary?: { pluginCount: number; mimeTypeCount: number; pluginNames?: string[] };
    notes?: string[];
};

function safe<T>(fn: () => T): Maybe<T> {
    try { return fn(); } catch { return null; }
}

// Storage feature tests (work on quirky TV runtimes)
function canUseLocalStorage(): boolean {
    try {
        const key = "__cap_ls__";
        window.localStorage.setItem(key, "1");
        window.localStorage.removeItem(key);
        return true;
    } catch { return false; }
}
function canUseSessionStorage(): boolean {
    try {
        const key = "__cap_ss__";
        window.sessionStorage.setItem(key, "1");
        window.sessionStorage.removeItem(key);
        return true;
    } catch { return false; }
}
function canUseIDB(): boolean {
    try { return "indexedDB" in window; } catch { return false; }
}

function canPlay(el: HTMLMediaElement, type: string, strict = false): boolean {
    try {
        const res = el.canPlayType(type);
        if (!res) return false;
        return strict ? res === "probably" : res === "probably" || res === "maybe";
    } catch { return false; }
}

// ---------- TV / platform detection (improved) ----------
function detectTv(ua: string) {
    // Direct objects (where available)
    const hasTizenObj = typeof (window as any).tizen !== "undefined";
    const hasWebOSObj = typeof (window as any).webOS !== "undefined";

    // Accept both "Tizen/5.5" and "Tizen 5.5"
    const tizenMatch = /Tizen(?:\/|\s)([\d.]+)/i.exec(ua);
    const tizen = hasTizenObj ? (["Tizen 999", "999"] as any) : tizenMatch;

    // Accept both "webOS/3.5" and "webOS 3.5" (and the "Web0S" variant)
    const webosMatch = /(Web0S|webOS|webOS\.TV)[\/\s]([\d.]+)/i.exec(ua);
    const webos = hasWebOSObj && !webosMatch ? (["webOS 999", "webOS", "999"] as any) : webosMatch;

    const orsay = /SMART-TV|Maple|Orsay/i.test(ua);
    const hbbtv = /HbbTV\/([\d.]+)/i.exec(ua);
    const operatv = /Opera TV|NETTV/i.test(ua);

    const roku = /Roku|DVP-|RokuOS/i.test(ua);

    // Chromecast receiver vs Google TV (Android TV branding)
    const chromecastReceiver = /(CrKey|Chromecast)/i.test(ua) && !/GoogleTV/i.test(ua);

    // Fire TV: Silk browser or AFT* device codes
    const isSilk = /Silk\/\d+/i.test(ua);
    const aftMatch = /\b(AFT[0-9A-Z]+)\b/i.exec(ua);
    const isAFT = !!aftMatch;
    const fireTV = isAFT;

    // Android TV (include GoogleTV token, avoid counting Chromecast receiver)
    const androidTVTokens = /(Android TV|GoogleTV|BRAVIA|SHIELD|MiBOX|KD-\d|PHILIPS TV|SMART-TV|AOSP on IA Emulator)/i.test(ua);
    const androidTV =
        androidTVTokens &&
        /Android|GoogleTV/i.test(ua) &&
        !chromecastReceiver &&
        !tizenMatch && !webosMatch; 

    // Android WebView (exclude Silk, which is a different engine)
    const isAndroid = /android/i.test(ua);
    const isAndroidWebView =
        isAndroid &&
        !isSilk &&
        (/;\s*wv[\);]/i.test(ua) ||
            /Version\/\d+\.\d+\s+Chrome\/[\d.]+\s+Mobile\s+Safari\/[\d.]+/i.test(ua));

    let forceLegacy = false;
    let reason: string | undefined;

    if (orsay) { forceLegacy = true; reason = "Samsung Orsay"; }

    if (tizen) {
        const ver = parseFloat((tizen as any)[1]);
        if (!Number.isNaN(ver) && ver < 4) { forceLegacy = true; reason = "Old Tizen"; }
    }

    if (webos) {
        const ver = parseFloat((webos as any)[2]);
        if (!Number.isNaN(ver) && ver < 4) { forceLegacy = true; reason = "Old webOS"; }
    }

    return {
        isTizen: tizen ? (tizen as any)[1] : null,
        isWebOS: webos ? (webos as any)[2] : null,
        isOrsay: orsay,
        isHbbTV: hbbtv ? hbbtv[1] : null,
        isOperaTV: operatv,
        isRoku: roku,
        isAmazonSilk: isSilk,
        isAndroidWebView: isAndroidWebView,
        isChromecast: chromecastReceiver,
        isAndroidTV: !!androidTV,
        isFireTV: fireTV,
        fireTVModel: aftMatch ? aftMatch[1] : null,
        forceLegacy,
        reason,
    };
}

function createGL(): WebGLRenderingContext | WebGL2RenderingContext | null {
    const canvas = document.createElement("canvas");
    try {
        // Try WebGL2 first, then WebGL1
        return (canvas.getContext("webgl2") as WebGL2RenderingContext)
            || (canvas.getContext("webgl") as WebGLRenderingContext)
            || null;
    } catch {
        return null;
    }
}

// Don't rely on WebGL2RenderingContext constructor existing on all engines
function isWebGL2(gl: WebGLRenderingContext | WebGL2RenderingContext | null): boolean {
    if (!gl) return false;
    // WebGL2 contexts expose getIndexedParameter; WebGL1 doesn't
    return typeof (gl as any).getIndexedParameter === "function";
}

function getWebGLInfoFrom(gl: WebGLRenderingContext | WebGL2RenderingContext | null): CapabilityReport["webglInfo"] {
    if (!gl) return undefined;

    const getParam = (p: number) => {
        try { return (gl as any).getParameter(p); } catch { return undefined; }
    };

    const dbg = safe(() => (gl as any).getExtension("WEBGL_debug_renderer_info"));
    const unmaskedVendor = dbg ? safe(() => (gl as any).getParameter((dbg as any).UNMASKED_VENDOR_WEBGL)) as string | null : null;
    const unmaskedRenderer = dbg ? safe(() => (gl as any).getParameter((dbg as any).UNMASKED_RENDERER_WEBGL)) as string | null : null;

    return {
        vendor: (unmaskedVendor ?? (getParam((gl as any).VENDOR) as string)) || undefined,
        renderer: (unmaskedRenderer ?? (getParam((gl as any).RENDERER) as string)) || undefined,
        version: (getParam((gl as any).VERSION) as string) || undefined,
        shadingLanguageVersion: (getParam((gl as any).SHADING_LANGUAGE_VERSION) as string) || undefined,
        extensions: safe(() => (gl as any).getSupportedExtensions?.() || []) || [],
    };
}

function disposeGL(gl: WebGLRenderingContext | WebGL2RenderingContext | null) {
    try {
        const lose = (gl as any)?.getExtension?.("WEBGL_lose_context");
        lose?.loseContext();
        // Drop bitmap memory too (optional)
        const canvas = (gl as any)?.canvas as HTMLCanvasElement | undefined;
        if (canvas) { canvas.width = 0; canvas.height = 0; }
    } catch { /* ignore */ }
}

// ----- WebAssembly detection -----

type WasmSupport = {
    hasWebAssembly: boolean;
    validateWorks: boolean;
    instantiateWorks: boolean;
    instantiateStreaming: boolean;
    threads: {
        available: boolean; // engine can construct shared Memory
        usable: boolean;    // current context allows usage (SAB + COI + Atomics)
        reason?: string;
    };
};

const EMPTY_WASM = new Uint8Array([
    0x00, 0x61, 0x73, 0x6d, // \0asm
    0x01, 0x00, 0x00, 0x00  // version 1
]);

async function detectWasmSupport(): Promise<WasmSupport> {
    const result: WasmSupport = {
        hasWebAssembly: typeof (window as any).WebAssembly === "object",
        validateWorks: false,
        instantiateWorks: false,
        instantiateStreaming: false,
        threads: { available: false, usable: false },
    };

    if (!result.hasWebAssembly) return result;

    try {
        result.validateWorks = (WebAssembly as any).validate?.(EMPTY_WASM) === true;
    } catch { /* older engines may not have validate */ }

    try {
        const mod = await WebAssembly.compile(EMPTY_WASM);
        const inst = await WebAssembly.instantiate(mod);
        result.instantiateWorks = !!inst;
    } catch {
        result.instantiateWorks = false;
    }

    result.instantiateStreaming = typeof (WebAssembly as any).instantiateStreaming === "function";

    // Threads: engine ability to construct shared memories
    try {
        const mem = new (WebAssembly as any).Memory({ initial: 1, maximum: 1, shared: true });
        result.threads.available = mem instanceof (WebAssembly as any).Memory;
    } catch {
        result.threads.available = false;
    }

    // Threads usable in this context
    const sab = typeof (window as any).SharedArrayBuffer !== "undefined";
    const coi = (window as any).crossOriginIsolated === true;
    const atomics = typeof (window as any).Atomics !== "undefined";

    if (result.threads.available && sab && coi && atomics) {
        try {
            const mem = new (WebAssembly as any).Memory({ initial: 1, maximum: 1, shared: true });
            result.threads.usable = mem instanceof (WebAssembly as any).Memory;
        } catch {
            result.threads.usable = false;
            result.threads.reason = "Engine supports threads, but shared Memory construction failed";
        }
    } else {
        result.threads.usable = false;
        const reasons: string[] = [];
        if (!result.threads.available) reasons.push("engine-no-shared-memory");
        if (!sab) reasons.push("no-SharedArrayBuffer");
        if (!coi) reasons.push("not-crossOriginIsolated (needs COOP/COEP)");
        if (!atomics) reasons.push("no-Atomics");
        if (reasons.length) result.threads.reason = reasons.join(", ");
    }

    return result;
}

async function getBattery() {
    try {
        const anyNav = navigator as any;
        if (anyNav.getBattery) {
            const b = await anyNav.getBattery();
            return { charging: !!b.charging, level: typeof b.level === "number" ? b.level : undefined };
        }
    } catch { /* ignored */ }
    return undefined;
}

async function getStorage() {
    try {
        const persisted = await (navigator as any).storage?.persisted?.();
        const estimate = await (navigator as any).storage?.estimate?.();
        return {
            quotaBytes: estimate?.quota ?? undefined,
            usageBytes: estimate?.usage ?? undefined,
            persisted: !!persisted,
        };
    } catch { return undefined; }
}

async function getPermissionsSummary(): Promise<Record<string, string>> {
    const result: Record<string, string> = {};
    const candidates = ["geolocation", "notifications", "camera", "microphone", "midi", "clipboard-read", "clipboard-write", "persistent-storage"];
    if (!("permissions" in navigator) || typeof (navigator as any).permissions.query !== "function") {
        return result;
    }
    await Promise.all(candidates.map(async (name) => {
        try {
            const st = await (navigator as any).permissions.query({ name } as any);
            result[name] = st.state;
        } catch {
            result[name] = "unknown";
        }
    }));
    return result;
}

function getUADataSync() {
    const anyNav = navigator as any;
    const uaData = anyNav.userAgentData;
    if (!uaData) return undefined;
    return {
        brands: uaData.brands || undefined,
        mobile: uaData.mobile ?? undefined,
        platform: uaData.platform ?? undefined,
    };
}

async function getUADataAsync() {
    const anyNav = navigator as any;
    const uaData = anyNav.userAgentData;
    if (!uaData || typeof uaData.getHighEntropyValues !== "function") return undefined;
    try {
        const vals = await uaData.getHighEntropyValues(["architecture", "bitness", "model", "platformVersion", "fullVersionList"]);
        return {
            brands: uaData.brands,
            mobile: uaData.mobile,
            platform: uaData.platform,
            architecture: vals.architecture,
            model: vals.model,
            bitness: vals.bitness,
            platformVersion: vals.platformVersion,
            fullVersionList: vals.fullVersionList,
        };
    } catch {
        return getUADataSync();
    }
}

function classifyTier(features: CapabilityReport["features"]): "legacy" | "baseline" | "modern" {
    let tier: "legacy" | "baseline" | "modern" = "legacy";
    if (features.promises && features.fetch && (features.webgl2 || features.webgl1) && features.wasm && features.mse) {
        tier = "baseline";
    }
    if (features.wasm && features.webgl2 && features.css.grid && features.serviceWorker) {
        tier = "modern";
    }
    return tier;
}

function getMediaSupport() {
    const v = document.createElement("video");
    const a = document.createElement("audio");
    const eme = "requestMediaKeySystemAccess" in navigator;

    const video = {
        h264: canPlay(v, 'video/mp4; codecs="avc1.42E01E"'),
        vp9: canPlay(v, 'video/webm; codecs="vp9"'),
        av1: canPlay(v, 'video/mp4; codecs="av01.0.05M.08"') || canPlay(v, 'video/webm; codecs="av1"'),
        hevc: canPlay(v, 'video/mp4; codecs="hvc1.1.6.L93.B0"') || canPlay(v, 'video/mp4; codecs="hev1"'),
        hls:
            canPlay(v, 'application/vnd.apple.mpegURL') ||
            canPlay(v, 'application/x-mpegURL') ||
            canPlay(v, 'audio/mpegurl'),
    };
    const audio = {
        aac: canPlay(a, 'audio/mp4; codecs="mp4a.40.2"'),
        mp3: canPlay(a, 'audio/mpeg'),
        opus: canPlay(a, 'audio/webm; codecs="opus"'),
        flac: canPlay(a, 'audio/flac') || canPlay(a, 'audio/x-flac'),
        vorbis: canPlay(a, 'audio/ogg; codecs="vorbis"'),
    };

    const emeKeySystems = {
        widevine: eme ? "maybe" as const : false,
        playready: eme ? "maybe" as const : false,
        fairplay: eme ? "maybe" as const : false,
    };

    return {
        video, audio,
        mediaCapabilities: { supported: "mediaCapabilities" in navigator },
        emeKeySystems,
    };
}

// ---------- Derived device type ----------
type DerivedType = CapabilityReport["derivedDeviceType"];

function classifyDerivedDeviceType(
    ua: string,
    uaData: CapabilityReport["userAgentData"] | undefined,
    tv: CapabilityReport["tvHeuristics"],
    env: CapabilityReport["environment"]
): DerivedType {
    const uaL = ua.toLowerCase();

    if (tv.isFireTV && tv.isAmazonSilk) return "FireTV (Silk)";        // Fire TV browser
    if (tv.isFireTV && tv.isAndroidWebView) return "FireTV (WebView)";   // Your Fire TV app

    // TV-first fallbacks
    if (tv.isRoku) return "Roku";
    if (tv.isTizen) return "Tizen";
    if (tv.isWebOS) return "WebOS";
    if (tv.isChromecast) return "Chromecast";
    if (tv.isFireTV) return "FireTV";
    if (tv.isAndroidTV) return "AndroidTV";

    // ---- existing desktop/mobile/tablet logic continues unchanged ----
    const chPlatform = (uaData?.platform || "").toLowerCase();
    const envPlatform = (env.platform || "").toLowerCase();

    const isAndroidCH = chPlatform.includes("android");
    const isCrOSCH = chPlatform.includes("chrome os") || chPlatform.includes("chromeos");
    const isWindowsCH = chPlatform.includes("windows");
    const isMacCH = chPlatform.includes("mac");
    const isLinuxCH = chPlatform.includes("linux");

    const isWindowsEnv = envPlatform.includes("win");
    const isMacEnv = envPlatform.includes("mac");
    const isLinuxEnv = envPlatform.includes("linux");
    const isCrOSEnv = envPlatform.includes("cros");

    const isWindowsUA = /windows nt|win64|win32/.test(uaL);
    const isMacUA = /macintosh|mac os x/.test(uaL);
    const isLinuxUA = /linux/.test(uaL) && !/android/.test(uaL);
    const isCrOSUA = /cros/.test(uaL);

    const touch = (env.maxTouchPoints ?? 0) > 0;
    const longSide = Math.max(env.screen.width ?? 0, env.screen.height ?? 0);

    const isIPhoneLike = /iphone/.test(uaL);
    const isIPadLike = /ipad|tablet/.test(uaL) || (/macintosh/.test(uaL) && touch);

    if (isAndroidCH || /android/.test(uaL)) {
        if (/tv|bravia|aft|shield|mibox/.test(uaL)) return "AndroidTV";
        if (touch && longSide >= 900) return "Tablet";
        return "Mobile";
    }

    if (isIPhoneLike) return "Mobile";
    if (isIPadLike) return "Tablet";

    if (isCrOSCH || isCrOSEnv || isCrOSUA) return "Desktop";

    if (isWindowsCH || isWindowsEnv || isWindowsUA) return "Desktop";
    if (isMacCH || isMacEnv || isMacUA) return "Desktop";
    if (isLinuxCH || isLinuxEnv || isLinuxUA) return "Desktop";

    if (uaData?.mobile) return "Mobile";
    return "Other";
}

export async function collectCapabilities(): Promise<CapabilityReport> {
    const ua = navigator.userAgent || "";
    const tv = detectTv(ua);
    const wasm = await detectWasmSupport();
    const gl = safe(createGL) || null;
    const hasWebGL2 = isWebGL2(gl);
    const hasWebGL1 = !!gl; // any context implies at least WebGL1

    const features = {
        // Core
        promises: typeof Promise !== "undefined",
        fetch: typeof fetch !== "undefined",

        // WASM
        wasm: wasm.hasWebAssembly && wasm.instantiateWorks,
        wasmValidate: wasm.validateWorks,
        wasmInstantiateStreaming: wasm.instantiateStreaming,
        wasmThreadsAvailable: wasm.threads.available,
        wasmThreadsUsable: wasm.threads.usable,
        sharedArrayBuffer: typeof (window as any).SharedArrayBuffer !== "undefined",
        crossOriginIsolated: (window as any).crossOriginIsolated === true,

        // Graphics
        webgl1: hasWebGL1,
        webgl2: hasWebGL2,
        webgpu: "gpu" in navigator,

        // Platform
        mse: typeof (window as any).MediaSource !== "undefined",
        eme: !!(navigator as any).requestMediaKeySystemAccess,
        serviceWorker: "serviceWorker" in navigator,
        notifications: "Notification" in window,
        webAudio: "AudioContext" in window || "webkitAudioContext" in window,
        offscreenCanvas: "OffscreenCanvas" in window,
        webRTC: "RTCPeerConnection" in window,
        idb: canUseIDB(),
        localStorage: canUseLocalStorage(),
        sessionStorage: canUseSessionStorage(),

        // CSS
        css: {
            grid: !!(window as any).CSS?.supports?.("display", "grid"),
            flexGap: !!(window as any).CSS?.supports?.("gap", "1px"),
            sticky: !!(window as any).CSS?.supports?.("position", "sticky"),
        },
    };

    const classificationTier = tv.forceLegacy ? "legacy" : classifyTier(features);

    const environment = {
        platform: safe(() => navigator.platform) ?? null,
        vendor: safe(() => navigator.vendor) ?? null,
        languages: safe(() => Array.from(navigator.languages || [])) || [],
        language: safe(() => navigator.language) ?? null,
        hardwareConcurrency: safe(() => navigator.hardwareConcurrency) ?? null,
        deviceMemoryGB: safe(() => (navigator as any).deviceMemory) ?? null,
        maxTouchPoints: safe(() => navigator.maxTouchPoints) ?? null,
        pointerTypes: ["none", "coarse", "fine"].filter(
            p => typeof window.matchMedia === "function" && window.matchMedia(`(pointer: ${p})`).matches
        ),
        hoverSupport: ["hover", "none"].filter(
            h => typeof window.matchMedia === "function" && window.matchMedia(`(hover: ${h})`).matches
        ),
        cookieEnabled: safe(() => navigator.cookieEnabled) ?? false,
        doNotTrack: safe(() => navigator.doNotTrack) ?? null,
        timezone: safe(() => Intl.DateTimeFormat().resolvedOptions().timeZone) ?? null,
        colorDepth: safe(() => window.screen?.colorDepth) ?? null,
        pixelRatio: safe(() => window.devicePixelRatio) ?? null,
        screen: {
            width: safe(() => window.screen?.width) ?? null,
            height: safe(() => window.screen?.height) ?? null,
            availWidth: safe(() => window.screen?.availWidth) ?? null,
            availHeight: safe(() => window.screen?.availHeight) ?? null,
        },
        window: {
            innerWidth: safe(() => window.innerWidth) ?? null,
            innerHeight: safe(() => window.innerHeight) ?? null,
        },
    };

    async function collectAppMemory() {
        const out: {
            jsHeapUsedBytes?: number;
            jsHeapTotalBytes?: number;
            jsHeapLimitBytes?: number;
            uaSpecificTotalBytes?: number;
            wasmHeapBytes?: number;
            jsHeapApproxFreeBytes?: number;
        } = {};

        // 1) Chrome/Chromium-only JS heap stats (non-standard)
        try {
            const pmem: any = (performance as any).memory;
            if (pmem && typeof pmem.usedJSHeapSize === "number") {
                out.jsHeapUsedBytes = pmem.usedJSHeapSize;
                out.jsHeapTotalBytes = pmem.totalJSHeapSize;
                out.jsHeapLimitBytes = pmem.jsHeapSizeLimit;
                if (typeof pmem.jsHeapSizeLimit === "number" && typeof pmem.usedJSHeapSize === "number") {
                    out.jsHeapApproxFreeBytes = Math.max(0, pmem.jsHeapSizeLimit - pmem.usedJSHeapSize);
                }
            }
        } catch { /* ignore */ }

        // 2) Experimental: per-origin memory (Chrome, secure context)
        try {
            const fn = (performance as any).measureUserAgentSpecificMemory;
            if (typeof fn === "function") {
                const res = await fn.call(performance);
                // res.bytes is total for this origin; res.breakdown is an array
                if (res && typeof res.bytes === "number") {
                    out.uaSpecificTotalBytes = res.bytes;
                }
            }
        } catch { /* ignore */ }

        // 3) Unity/WebAssembly heap size (if Unity exposes Module)
        try {
            const mod: any = (window as any).Module;
            // Unity (Emscripten) typically exposes HEAP8 and/or wasmMemory
            const wasmBuf: ArrayBuffer | undefined =
                mod?.HEAP8?.buffer || mod?.wasmMemory?.buffer || (mod?._wasmMemory?.buffer);
            if (wasmBuf && typeof wasmBuf.byteLength === "number") {
                out.wasmHeapBytes = wasmBuf.byteLength;
            }
        } catch { /* ignore */ }

        return out;
    }

    const report: CapabilityReport = {
        timestamps: { collectedAtIso: new Date().toISOString() },
        userAgent: ua,
        userAgentData: getUADataSync(),
        tvHeuristics: tv,
        derivedDeviceType: "Other", // temp; set properly after async UA-CH
        classification: { tier: classificationTier },
        environment,
        features,
        webglInfo: hasWebGL1 ? getWebGLInfoFrom(gl) : undefined,
        mediaSupport: getMediaSupport(),
        network: safe(() => {
            const c = (navigator as any).connection || (navigator as any).mozConnection || (navigator as any).webkitConnection;
            return c ? {
                effectiveType: c.effectiveType,
                downlinkMbps: typeof c.downlink === "number" ? c.downlink : undefined,
                rttMs: typeof c.rtt === "number" ? c.rtt : undefined,
                saveData: !!c.saveData,
            } : undefined;
        }),
        pluginsSummary: safe(() => ({
            pluginCount: navigator.plugins ? navigator.plugins.length : 0,
            mimeTypeCount: navigator.mimeTypes ? navigator.mimeTypes.length : 0,
            pluginNames: Array.from(navigator.plugins || []).slice(0, 10).map((p: any) => p.name),
        })) || { pluginCount: 0, mimeTypeCount: 0 },
        notes: [],
    };

    // Async enrichers
    const [battery, storage, permissions, highEntropyUA] = await Promise.all([
        getBattery(),
        getStorage(),
        getPermissionsSummary(),
        getUADataAsync(),
    ]);

    if (battery) report.battery = battery;
    if (storage) report.storage = storage;
    if (permissions) report.permissions = permissions;
    if (highEntropyUA) report.userAgentData = { ...(report.userAgentData || {}), ...highEntropyUA };

    // Derived device type (after CH enrichment)
    report.derivedDeviceType = classifyDerivedDeviceType(ua, report.userAgentData, report.tvHeuristics, report.environment);

    // Explanatory notes for quick triage
    if (!features.wasm) report.notes?.push("WebAssembly instantiate failed (baseline WASM not working)");
    if (wasm.threads.reason) report.notes?.push(`WASM threads unusable: ${wasm.threads.reason}`);
    if (!(features.webgl1 || features.webgl2)) report.notes?.push("WebGL not available");

    const appMemory = await collectAppMemory();
    if (appMemory && Object.keys(appMemory).length) {
        (report as any).appMemory = appMemory;
    }

    disposeGL(gl);

    return report;
}
